import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowLeftRight,
  ChevronRight,
  ClipboardCheck,
  Settings,
  Undo2,
} from "lucide-react";
import type { CurrentUser } from "@/features/auth/currentUserTypes";
import { todaySaoPaulo } from "@/lib/dates";
import {
  formatDashboardCount,
  formatDashboardCurrency,
  formatDashboardPercent,
  formatDashboardUnitQuantity,
} from "@/features/dashboard/metricFormatters";
import type { DashboardPeriodState } from "@/features/dashboard/period";
import type {
  DashboardCountMetric,
  DashboardDetailKind,
  DashboardMoneyMetric,
  DashboardRankingKind,
  DashboardRankingSort,
  DashboardSectionName,
  DashboardSupplierQualityItem,
  SuperDashboardAnalytics,
  SuperDashboardOperations,
} from "@/features/dashboard/types";
import {
  calculateDashboardCommissionCents,
  parseDashboardCommissionPercent,
  readDashboardCommissionPercent,
  writeDashboardCommissionPercent,
} from "@/components/dashboard/commissionConfig";
import { SuperPeriod } from "./SuperPeriod";
import { SuperTrend } from "./SuperTrend";
import "./SuperDashboard.css";

type RankingReturnTarget = {
  kind: DashboardRankingKind;
  sort: DashboardRankingSort;
};
export type SuperDashboardPanelRequest =
  | {
      mode: "ranking";
      kind: DashboardRankingKind;
      sort: DashboardRankingSort;
      title: string;
    }
  | {
      mode: "detail";
      kind: Exclude<DashboardDetailKind, "load">;
      id: string;
      title: string;
      returnTo: RankingReturnTarget;
    }
  | { mode: "quality" };

export interface SuperDashboardProps {
  user: CurrentUser;
  analytics: SuperDashboardAnalytics;
  operations?: SuperDashboardOperations;
  period: DashboardPeriodState;
  pendingPeriod?: DashboardPeriodState | null;
  updating?: boolean;
  onPeriodChange: (period: DashboardPeriodState) => void;
  onNavigate: (path: "/pedidos" | "/cargas") => void;
  onOpenPanel: (request: SuperDashboardPanelRequest) => void;
  onRetryAnalytics: () => void;
}

const amount = (cents: number) => formatDashboardCurrency(cents).full;
const count = (value: number) => formatDashboardCount(value).full;
const status = (
  analytics: SuperDashboardAnalytics,
  section: DashboardSectionName,
) => analytics.section_status?.find((row) => row.section === section)?.status;
function Unavailable({
  error,
  retry,
}: {
  error?: boolean;
  retry?: () => void;
}) {
  return (
    <p className="lnk209-empty" role="status">
      {error
        ? "Falha ao carregar esta seção."
        : "Dados indisponíveis para esta seção."}{" "}
      {error && retry && <button onClick={retry}>Tentar novamente</button>}
    </p>
  );
}

function Commission({
  userId,
  sales,
}: {
  userId: string;
  sales: number | null;
}) {
  const [raw, setRaw] = useState(() =>
    String(readDashboardCommissionPercent(userId)),
  );
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const percent = parseDashboardCommissionPercent(raw);
  const value =
    percent === null || sales === null
      ? null
      : calculateDashboardCommissionCents(sales, percent);
  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    const close = (event: KeyboardEvent | PointerEvent) => {
      if (
        event.type === "keydown"
          ? (event as KeyboardEvent).key === "Escape"
          : !ref.current?.contains(event.target as Node)
      ) {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener("keydown", close);
    document.addEventListener("pointerdown", close);
    return () => {
      document.removeEventListener("keydown", close);
      document.removeEventListener("pointerdown", close);
    };
  }, [open]);
  return (
    <article ref={ref} className="lnk209-commission">
      <span className="lnk209-kpi-index">COMISSÃO SIMULADA</span>
      <strong title="simulação, não pagamento">
        {value === null ? "—" : amount(value)}
      </strong>
      <span className="lnk209-no-compare" aria-label="Simulação sem comparação">
        —
      </span>
      <button
        ref={button}
        className="lnk209-commission-gear"
        aria-label="Configurar comissão"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Settings size={17} aria-hidden="true" />
      </button>
      {open && (
        <div
          className="lnk209-commission-popover"
          role="dialog"
          aria-label="Configurar comissão simulada"
        >
          <label className="lnk209-commission-editor">
            Percentual de comissão simulada
            <span>
              <input
                ref={input}
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={raw}
                aria-invalid={percent === null}
                onChange={(event) => {
                  const next = event.target.value;
                  setRaw(next);
                  const parsed = parseDashboardCommissionPercent(next);
                  if (parsed !== null)
                    writeDashboardCommissionPercent(userId, parsed);
                }}
              />
              %
            </span>
          </label>
          <span>
            {percent === null ? "Informe 0 a 100%" : "simulação, não pagamento"}
          </span>
        </div>
      )}
    </article>
  );
}

function Kpi({
  label,
  metric,
  current,
  description,
  onClick,
  testId,
}: {
  label: string;
  metric: DashboardMoneyMetric | DashboardCountMetric;
  current?: boolean;
  description?: string;
  onClick: () => void;
  testId?: string;
}) {
  const value =
    metric.status !== "available"
      ? "—"
      : metric.unit === "cents"
        ? amount(metric.value)
        : count(metric.value);
  const comparison =
    metric.status === "available" ? metric.comparison : undefined;
  return (
    <article>
      <span className="lnk209-kpi-index">{label.toUpperCase()}</span>
      <strong data-testid={testId}>{value}</strong>
      {current ? (
        <span className="lnk209-no-compare">operação atual</span>
      ) : comparison ? (
        <span
          className={`lnk209-delta ${comparison.direction === "down" ? "negative" : comparison.direction === "same" ? "neutral" : "positive"}`}
        >
          {comparison.direction === "down"
            ? "↘ −"
            : comparison.direction === "same"
              ? "→ "
              : "↗ +"}
          {formatDashboardPercent(comparison.percentage).full.replace(
            /^[+-]/,
            "",
          )}
        </span>
      ) : (
        <span className="lnk209-no-compare" aria-label="Sem base comparável">
          —
        </span>
      )}
      {metric.status !== "available" && (
        <span className="lnk209-kpi-unavailable">
          {metric.status === "error" ? "Falha nos dados" : "Dado indisponível"}
        </span>
      )}
      <button
        className="lnk209-kpi-link"
        aria-label={`Abrir ${label}`}
        title={description}
        onClick={onClick}
      />
    </article>
  );
}

function LoadDonut({
  analytics,
  retry,
}: {
  analytics: SuperDashboardAnalytics;
  retry: () => void;
}) {
  const [active, setActive] = useState<string | null>(null);
  const situation = analytics.sections.load_situation;
  const unavailable = status(analytics, "load_situation");
  const states = (
    [
      { state: "concluded", label: "Entregues", color: "#327554" },
      { state: "in_progress", label: "Em carregamento", color: "#d5a145" },
      { state: "not_started", label: "Pendentes", color: "#a8b6a7" },
    ] as const
  ).map((item) => ({
    ...item,
    count: situation.states.find((row) => row.state === item.state)?.count ?? 0,
  }));
  let offset = 0;
  return (
    <section
      className="lnk209-panel lnk209-load"
      aria-label="Situação das cargas"
    >
      <header>
        <div>
          <h2>Situação das cargas</h2>
        </div>
        <span className="lnk209-panel-meta">por quantidade</span>
      </header>
      {unavailable && unavailable !== "available" ? (
        <Unavailable error={unavailable === "error"} retry={retry} />
      ) : situation.total ? (
        <>
          <div className="lnk209-load-body">
            <div
              className="lnk209-donut"
              role="img"
              aria-label={`${situation.total} cargas: ${states.map((item) => `${item.count} ${item.label}`).join(", ")}`}
            >
              <svg viewBox="0 0 120 120" aria-hidden="true">
                <circle
                  cx="60"
                  cy="60"
                  r="48"
                  fill="none"
                  stroke="var(--track)"
                  strokeWidth="14"
                />
                {states.map((item) => {
                  const start = offset;
                  const slice = (item.count / situation.total) * 100;
                  offset += slice;
                  return (
                    <circle
                      key={item.state}
                      cx="60"
                      cy="60"
                      r="48"
                      fill="none"
                      stroke={item.color}
                      strokeWidth="14"
                      pathLength="100"
                      strokeDasharray={`${slice} ${100 - slice}`}
                      strokeDashoffset={-start}
                      transform="rotate(-90 60 60)"
                    />
                  );
                })}
              </svg>
              <div>
                <strong>{count(situation.total)}</strong>
                <small>CARGAS</small>
              </div>
            </div>
            <div className="lnk209-legend">
              {states.map((item) => (
                <button
                  key={item.state}
                  aria-label={item.label}
                  aria-pressed={active === item.label}
                  onMouseEnter={() => setActive(item.label)}
                  onFocus={() => setActive(item.label)}
                  onClick={() => setActive(item.label)}
                >
                  <i style={{ background: item.color }} />
                  <span>{item.label}</span>
                  <strong>{count(item.count)}</strong>
                </button>
              ))}
            </div>
          </div>
          <p className="lnk209-detail" role="status">
            {active
              ? `${active}: ${count(states.find((item) => item.label === active)!.count)} cargas`
              : "Selecione um estado para conferir a quantidade"}
          </p>
        </>
      ) : (
        <p className="lnk209-empty">
          Nenhuma carga registrada neste período. Explore outra data.
        </p>
      )}
    </section>
  );
}

type RankRow = { id?: string; name: string; value: string; detail?: string };
function Ranking({
  title,
  rows,
  kind,
  sort,
  section,
  analytics,
  onOpen,
  retry,
}: {
  title: string;
  rows?: RankRow[];
  kind: DashboardRankingKind;
  sort: DashboardRankingSort;
  section: DashboardSectionName;
  analytics: SuperDashboardAnalytics;
  onOpen: (request: SuperDashboardPanelRequest) => void;
  retry: () => void;
}) {
  const availability = status(analytics, section);
  const blocked = !rows || (availability && availability !== "available");
  const request: SuperDashboardPanelRequest = {
    mode: "ranking",
    kind,
    sort,
    title,
  };
  const open = (row?: RankRow) => {
    if (!row?.id) return onOpen(request);
    onOpen({
      mode: "detail",
      kind: (
        {
          clients: "client",
          suppliers: "supplier",
          stores: "store",
          products: "product",
          checkers: "checker",
        } as const
      )[kind],
      id: row.id,
      title: row.name,
      returnTo: { kind, sort },
    });
  };
  return (
    <section className="lnk209-panel lnk209-ranking" aria-label={title}>
      <header>
        <button
          className="lnk209-ranking-heading"
          disabled={Boolean(blocked)}
          onClick={() => onOpen(request)}
        >
          <h2>{title}</h2>
        </button>
        <button
          className="lnk209-view-all"
          disabled={Boolean(blocked)}
          onClick={() => onOpen(request)}
        >
          Ver mais <ChevronRight size={15} aria-hidden="true" />
        </button>
      </header>
      {blocked ? (
        <Unavailable error={availability === "error"} retry={retry} />
      ) : rows.length ? (
        <div className="lnk209-ranking-list">
          {rows.slice(0, 3).map((row, index) => (
            <button
              key={`${row.id ?? row.name}-${index}`}
              aria-label={row.name}
              onClick={() => open(row)}
            >
              <span className="lnk209-rank">
                {String(index + 1).padStart(2, "0")}
              </span>
              <span className="lnk209-ranking-name">
                <strong>{row.name}</strong>
                {row.detail && <small>{row.detail}</small>}
              </span>
              <strong className="lnk209-ranking-value">{row.value}</strong>
            </button>
          ))}
        </div>
      ) : (
        <p className="lnk209-empty">Sem dados observados neste período.</p>
      )}
    </section>
  );
}

const weekday = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
function Heatmap({
  analytics,
  retry,
}: {
  analytics: SuperDashboardAnalytics;
  retry: () => void;
}) {
  const cells = analytics.sections.sales_heatmap;
  const availability = status(analytics, "sales_heatmap");
  const from = analytics.period.from;
  const to = analytics.period.to;
  const range = from
    ? `${from.slice(8, 10)}/${from.slice(5, 7)}${from.slice(0, 4) === to.slice(0, 4) ? "" : `/${from.slice(0, 4)}`}–${to.slice(8, 10)}/${to.slice(5, 7)}/${to.slice(0, 4)}`
    : to;
  const multiMonth = Boolean(from && from.slice(0, 7) !== to.slice(0, 7));
  const first = new Date(`${to.slice(0, 7)}-01T12:00:00Z`);
  const firstMonday = Date.UTC(
    first.getUTCFullYear(),
    first.getUTCMonth(),
    1 - ((first.getUTCDay() + 6) % 7),
  );
  const byKey = new Map(
    cells?.map((cell) => [`${cell.week_of_month}-${cell.weekday}`, cell]),
  );
  const totals = weekday.map((_, index) =>
    (cells ?? [])
      .filter((cell) => cell.weekday === index + 1)
      .reduce(
        (sum, cell) => ({
          cents: sum.cents + cell.total_value_cents,
          days: sum.days + cell.days_with_orders,
        }),
        { cents: 0, days: 0 },
      ),
  );
  const maxDay = Math.max(
    0,
    ...totals.map((total) => (total.days ? total.cents / total.days : 0)),
  );
  const maxCell = Math.max(
    1,
    ...(cells ?? []).map(
      (cell) => cell.total_value_cents / cell.days_with_orders,
    ),
  );
  return (
    <section className="lnk209-panel lnk209-heat" aria-label="Ritmo de vendas">
      <header>
        <div>
          <h2>Ritmo de vendas</h2>
        </div>
        <span className="lnk209-panel-meta">
          {multiMonth
            ? range
            : first.toLocaleDateString("pt-BR", {
                month: "long",
                year: "numeric",
                timeZone: "UTC",
              })}
        </span>
      </header>
      {!cells || (availability && availability !== "available") ? (
        <Unavailable error={availability === "error"} retry={retry} />
      ) : cells.length === 0 ? (
        <p className="lnk209-empty">Sem dados observados neste período.</p>
      ) : (
        <div
          className="lnk209-heat-grid"
          role="grid"
          aria-label={`Média de vendas por semana do mês e dia da semana, ${range}`}
        >
          <div className="lnk209-week-row" role="row">
            <span className="lnk209-week-head" />
            {weekday.map((day, index) => (
              <span
                key={day}
                className={`lnk209-week-head${maxDay && totals[index]!.days && totals[index]!.cents / totals[index]!.days === maxDay ? " strongest" : ""}`}
                role="columnheader"
              >
                {day}
              </span>
            ))}
          </div>
          {Array.from({ length: 6 }, (_, week) => (
            <div className="lnk209-week-row" role="row" key={week}>
              <span className="lnk209-week-index" role="rowheader">
                S{week + 1}
              </span>
              {weekday.map((day, index) => {
                const cell = byKey.get(`${week + 1}-${index + 1}`);
                const average = cell
                  ? cell.total_value_cents / cell.days_with_orders
                  : 0;
                const observedDate = new Date(
                  firstMonday + (week * 7 + index) * 86400000,
                )
                  .toISOString()
                  .slice(0, 10);
                const truthfulDate =
                  !multiMonth &&
                  observedDate.slice(0, 7) === to.slice(0, 7) &&
                  observedDate >= (from ?? observedDate) &&
                  observedDate <= to &&
                  cell?.days_with_orders === 1;
                const label = cell
                  ? `${truthfulDate ? `${observedDate.slice(8, 10)}/${observedDate.slice(5, 7)}/${observedDate.slice(0, 4)}` : `Semana ${week + 1}, ${day}`}: média ${amount(Math.round(average))} por dia com pedidos; total ${amount(cell.total_value_cents)} em ${cell.days_with_orders} ${cell.days_with_orders === 1 ? "dia observado" : "dias observados"}`
                  : `Semana ${week + 1}, ${day}: sem observação`;
                return cell ? (
                  <button
                    key={day}
                    role="gridcell"
                    aria-label={label}
                    title={label}
                    data-level={
                      average > 0
                        ? Math.max(1, Math.ceil((average / maxCell) * 4))
                        : 0
                    }
                  >
                    {truthfulDate ? Number(observedDate.slice(8, 10)) : "·"}
                  </button>
                ) : (
                  <span
                    key={day}
                    className="lnk209-outside"
                    role="gridcell"
                    aria-label={label}
                  />
                );
              })}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Quality({
  analytics,
  retry,
  open,
}: {
  analytics: SuperDashboardAnalytics;
  retry: () => void;
  open: () => void;
}) {
  const [active, setActive] = useState<string | null>(null);
  const unavailable = status(analytics, "quality_outcomes");
  const outcomes = analytics.sections.quality_outcomes;
  const rows = [
    { name: "Decisões", value: outcomes.decision_count, icon: ClipboardCheck },
    {
      name: "Substituídos",
      value: outcomes.substituted_count,
      icon: ArrowLeftRight,
    },
    { name: "Devolvidos", value: outcomes.returned_count, icon: Undo2 },
    {
      name: "Divergências",
      value: outcomes.divergence_count,
      icon: AlertTriangle,
    },
  ];
  return (
    <section
      className="lnk209-panel lnk209-quality-preview"
      aria-label="Qualidade da operação"
    >
      <header>
        <h2>Qualidade da operação</h2>
      </header>
      {unavailable && unavailable !== "available" ? (
        <Unavailable error={unavailable === "error"} retry={retry} />
      ) : (
        <div className="lnk209-quality-ledger">
          {rows.map(({ name, value, icon: Icon }) => (
            <button
              key={name}
              aria-label={`${name}: ${count(value)}`}
              aria-pressed={active === name}
              onMouseEnter={() => setActive(name)}
              onFocus={() => setActive(name)}
              onClick={() => {
                setActive(name);
                open();
              }}
            >
              <span className="lnk209-outcome-name">
                <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
                <span>{name}</span>
              </span>
              <strong>{count(value)}</strong>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

const unitLabel: Record<string, string> = {
  kg: "Peso avulso",
  cx: "Caixas",
  bdj: "Bandejas",
  sc: "Sacos",
  un: "Unidades",
};
function Breakdowns({
  analytics,
  retry,
}: {
  analytics: SuperDashboardAnalytics;
  retry: () => void;
}) {
  const [active, setActive] = useState<string | null>(null);
  const categories = analytics.sections.category_spend;
  const quantities = analytics.sections.purchased_quantities;
  const categoryStatus = status(analytics, "category_spend");
  const quantityStatus = status(analytics, "purchased_quantities");
  return (
    <div className="lnk209-breakdowns">
      <section
        className="lnk209-panel lnk209-category-spend"
        aria-label="Compras por categoria"
      >
        <header>
          <h2>Compras por categoria</h2>
        </header>
        {categoryStatus && categoryStatus !== "available" ? (
          <Unavailable error={categoryStatus === "error"} retry={retry} />
        ) : categories.length ? (
          <dl>
            {categories.map((item) => (
              <div key={item.category_name}>
                <dt>{item.category_name}</dt>
                <dd>{amount(item.value_cents)}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="lnk209-empty">Sem dados observados neste período.</p>
        )}
      </section>
      <section className="lnk209-panel" aria-label="Quantidade comprada">
        <header>
          <h2>Quantidade comprada</h2>
        </header>
        {quantityStatus && quantityStatus !== "available" ? (
          <Unavailable error={quantityStatus === "error"} retry={retry} />
        ) : quantities.length ? (
          <div className="lnk209-unit-ledger">
            {quantities.map(({ unit, quantity }) => {
              const formatted = formatDashboardUnitQuantity(unit, quantity);
              return (
                <button
                  key={unit}
                  aria-label={`${unit}: ${formatted?.full ?? "dado indisponível"} ${unit}`}
                  aria-pressed={active === unit}
                  title={`${formatted?.full ?? "—"} ${unit}`}
                  onMouseEnter={() => setActive(unit)}
                  onFocus={() => setActive(unit)}
                  onClick={() => setActive(unit)}
                >
                  <span className="lnk209-unit-name">
                    {unitLabel[unit] ?? unit}
                    <small>{unit}</small>
                  </span>
                  <strong>{formatted?.full ?? "—"}</strong>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="lnk209-empty">Sem dados observados neste período.</p>
        )}
      </section>
    </div>
  );
}

export function SuperDashboard({
  user,
  analytics,
  operations,
  period,
  pendingPeriod,
  updating,
  onPeriodChange,
  onNavigate,
  onOpenPanel,
  onRetryAnalytics,
}: SuperDashboardProps) {
  const openMetric: DashboardMoneyMetric = !operations
    ? {
        status: "unavailable",
        unit: "cents",
        reason_code: "current_operations",
      }
    : operations.section_status?.some(
          (item) =>
            item.section === "open_loads" && item.status !== "available",
        )
      ? {
          status: "unavailable",
          unit: "cents",
          reason_code: "current_operations",
        }
      : {
          status: "available",
          unit: "cents",
          value: operations.open_loads.total_value_cents,
        };
  const sales =
    analytics.metrics.sales.status === "available"
      ? analytics.metrics.sales.value
      : null;
  const suppliers = analytics.sections.supplier_sales?.map((item) => ({
    id: item.id,
    name: item.name,
    value: amount(item.value_cents),
  }));
  const stores = analytics.sections.best_stores?.map((item) => ({
    id: item.id,
    name: item.name,
    value: amount(item.value_cents),
  }));
  const clients =
    analytics.sections.supplier_sales && analytics.sections.best_stores
      ? analytics.sections.best_clients.map((item) => ({
          id: item.id,
          name: item.name,
          value: amount(item.value_cents),
        }))
      : undefined;
  const products = analytics.sections.leading_products.map((item) => ({
    id: item.id,
    name: item.name,
    value: item.quantities
      .map(
        (q) =>
          `${formatDashboardUnitQuantity(q.unit, q.quantity)?.full ?? "—"} ${q.unit}`,
      )
      .join(" · "),
  }));
  const qualityRows = (
    items: DashboardSupplierQualityItem[],
    field: "rate" | "impact",
  ) =>
    items.map((item) => ({
      id: item.supplier_id,
      name: item.supplier_name,
      value:
        field === "rate"
          ? formatDashboardPercent(item.return_rate).full
          : amount(item.impact_cents),
    }));
  const today = todaySaoPaulo();
  const context = new Date(`${today}T12:00:00Z`);
  const qualityAvailability = status(analytics, "quality_outcomes");
  const qualityMetric = (value: number): DashboardCountMetric =>
    qualityAvailability && qualityAvailability !== "available"
      ? { status: qualityAvailability, unit: "count" }
      : { status: "available", unit: "count", value };
  return (
    <main
      className="lnk209-super lnk209-app"
      data-testid="super-dashboard"
      aria-busy={updating || undefined}
    >
      <div className="lnk209-content">
        <div className="lnk209-inner">
          <header className="lnk209-main-head">
            <div>
              <h1>Painel comercial</h1>
            </div>
            <span className="lnk209-header-context">
              <span>
                {period.period === "custom"
                  ? "PERÍODO PERSONALIZADO"
                  : context
                      .toLocaleDateString("pt-BR", {
                        weekday: "long",
                        timeZone: "UTC",
                      })
                      .toUpperCase()}
              </span>
              <strong>
                {period.period === "custom" && period.from && period.to
                  ? `${period.from.slice(8, 10)}/${period.from.slice(5, 7)}–${period.to.slice(8, 10)}/${period.to.slice(5, 7)}/${period.to.slice(0, 4)}`
                  : context
                      .toLocaleDateString("pt-BR", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        timeZone: "UTC",
                      })
                      .toUpperCase()
                      .replaceAll(".", "")}
              </strong>
            </span>
          </header>
          <SuperPeriod
            period={period}
            pendingPeriod={pendingPeriod}
            metadata={analytics.period}
            updating={updating}
            onChange={onPeriodChange}
          />
          <section
            className="lnk209-kpis"
            aria-label="Indicadores comerciais principais"
            data-testid="super-dashboard-headlines"
          >
            <Kpi
              label="Vendas no período"
              metric={analytics.metrics.sales}
              description="Quantidades recebidas no período, mesmo com a carga em andamento."
              testId="sales-total"
              onClick={() => onNavigate("/pedidos")}
            />
            <Kpi
              label="Valor em aberto"
              metric={openMetric}
              description="Quantidades ainda aguardadas, independentemente do período selecionado."
              current
              onClick={() => onNavigate("/cargas")}
            />
            <Kpi
              label="Pedidos no período"
              metric={analytics.metrics.orders}
              onClick={() => onNavigate("/pedidos")}
            />
            <Commission userId={user.id} sales={sales} />
          </section>
          <div
            className="lnk209-analysis"
            data-testid="super-dashboard-analysis"
          >
            {status(analytics, "sales_evolution") &&
            status(analytics, "sales_evolution") !== "available" ? (
              <section className="lnk209-panel" aria-label="Evolução de vendas">
                <header>
                  <h2>Evolução de vendas</h2>
                </header>
                <Unavailable
                  error={status(analytics, "sales_evolution") === "error"}
                  retry={onRetryAnalytics}
                />
              </section>
            ) : (
              <SuperTrend
                points={analytics.sections.sales_evolution}
                period={period}
                metadata={analytics.period}
                onHistoryChange={(history) =>
                  onPeriodChange({ ...period, history })
                }
              />
            )}
            <LoadDonut analytics={analytics} retry={onRetryAnalytics} />
          </div>
          <div className="lnk209-bottom" data-testid="super-dashboard-quality">
            <Heatmap analytics={analytics} retry={onRetryAnalytics} />
            <Quality
              analytics={analytics}
              retry={onRetryAnalytics}
              open={() => onOpenPanel({ mode: "quality" })}
            />
          </div>
          <div
            className="lnk209-rankings"
            data-testid="super-dashboard-rankings"
          >
            <Ranking
              title="Fornecedores por vendas"
              rows={suppliers}
              kind="suppliers"
              sort="value"
              section="supplier_sales"
              analytics={analytics}
              onOpen={onOpenPanel}
              retry={onRetryAnalytics}
            />
            <Ranking
              title="Melhores clientes"
              rows={clients}
              kind="clients"
              sort="value"
              section="best_clients"
              analytics={analytics}
              onOpen={onOpenPanel}
              retry={onRetryAnalytics}
            />
            <Ranking
              title="Melhores lojas"
              rows={stores}
              kind="stores"
              sort="value"
              section="best_stores"
              analytics={analytics}
              onOpen={onOpenPanel}
              retry={onRetryAnalytics}
            />
            <Ranking
              title="Produtos em destaque"
              rows={products}
              kind="products"
              sort="value"
              section="leading_products"
              analytics={analytics}
              onOpen={onOpenPanel}
              retry={onRetryAnalytics}
            />
            <Ranking
              title="Fornecedores por taxa de retorno"
              rows={qualityRows(analytics.sections.supplier_quality, "rate")}
              kind="suppliers"
              sort="return_rate"
              section="supplier_quality"
              analytics={analytics}
              onOpen={onOpenPanel}
              retry={onRetryAnalytics}
            />
            <Ranking
              title="Fornecedores por impacto financeiro"
              rows={qualityRows(analytics.sections.supplier_impact, "impact")}
              kind="suppliers"
              sort="financial_impact"
              section="supplier_impact"
              analytics={analytics}
              onOpen={onOpenPanel}
              retry={onRetryAnalytics}
            />
          </div>
          <section
            className="lnk209-kpis lnk209-kpis-secondary"
            aria-label="Indicadores comerciais secundários"
            data-testid="super-dashboard-secondary-metrics"
          >
            <Kpi
              label="Ticket médio"
              metric={analytics.metrics.average_ticket}
              onClick={() => onNavigate("/pedidos")}
            />
            <Kpi
              label="Cargas concluídas"
              metric={analytics.metrics.completed_loads}
              onClick={() => onNavigate("/cargas")}
            />
            <Kpi
              label="Devoluções"
              metric={qualityMetric(
                analytics.sections.quality_outcomes.returned_count,
              )}
              onClick={() => onOpenPanel({ mode: "quality" })}
            />
            <Kpi
              label="Divergências"
              metric={qualityMetric(
                analytics.sections.quality_outcomes.divergence_count,
              )}
              onClick={() => onOpenPanel({ mode: "quality" })}
            />
          </section>
          <Breakdowns analytics={analytics} retry={onRetryAnalytics} />
        </div>
      </div>
    </main>
  );
}
